import type {
  AuthResult,
  Grant,
  RecursoDoPlano,
  SessionUser,
  TenantSummary,
} from '@levitas/contracts';
import { can } from '@levitas/contracts';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ContextoSessao, type EstadoSessao, type ValorSessao } from './contexto-sessao';
import { api, aoExpirarSessao, definirToken, renovarSessao } from './api';
import { definirModoSimplificado } from './tema';
import {
  acessoDeSuporteLembrado,
  esquecerAcessoDeSuporte,
  lembrarAcessoDeSuporte,
} from './acesso-de-suporte';
import { limparOffline } from './cache-offline';
import { esquecerSessao, lembrarSessao, sessaoLembrada } from './sessao-lembrada';

/** Permissões e recursos do plano, que a sessão carrega juntos. */
interface Acessos {
  grants: Grant[];
  recursos: Partial<Record<RecursoDoPlano, boolean>>;
}

const SEM_ACESSO: Acessos = { grants: [], recursos: {} };

const ESTADO_INICIAL: EstadoSessao = {
  carregando: true,
  usuario: null,
  igreja: null,
  igrejas: [],
  permissoes: [],
  recursos: {},
};

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<EstadoSessao>(ESTADO_INICIAL);

  /**
   * Quantas vezes a sessão mudou por decisão de alguém — entrar, sair, trocar
   * de igreja, adotar acesso de suporte.
   *
   * A restauração pelo cookie começa quando a página carrega e pode terminar
   * **depois** de a pessoa ter saído. Sem este contador, ela chegava atrasada e
   * repunha a sessão que acabara de ser encerrada: a tela voltava para dentro
   * do aplicativo, e quem saiu ficava logado. No tablet que passa de mão em
   * mão na secretaria, é a conta de uma pessoa aberta para a seguinte.
   */
  const geracao = useRef(0);
  const novaGeracao = useCallback(() => {
    geracao.current += 1;
  }, []);

  const carregarPermissoes = useCallback(async (): Promise<Acessos> => {
    try {
      const dados = await api<Acessos>('/me/abilities');
      return { grants: dados.grants, recursos: dados.recursos ?? {} };
    } catch {
      return { grants: [], recursos: {} };
    }
  }, []);

  const aplicar = useCallback(
    async (auth: AuthResult) => {
      novaGeracao();
      definirToken(auth.accessToken);
      /*
       * O modo simplificado é da pessoa, e o servidor é quem sabe. O
       * `localStorage` continua guardando o último valor conhecido — é ele que
       * evita a tela mudar de tamanho na cara de quem acabou de abrir o app.
       */
      definirModoSimplificado(auth.user.modoSimplificado);
      const acessos = auth.tenant ? await carregarPermissoes() : SEM_ACESSO;
      lembrarSessao({
        usuario: auth.user,
        igreja: auth.tenant,
        igrejas: auth.tenants,
        permissoes: acessos.grants,
        recursos: acessos.recursos,
      });
      setEstado({
        carregando: false,
        usuario: auth.user,
        igreja: auth.tenant,
        igrejas: auth.tenants,
        permissoes: acessos.grants,
        recursos: acessos.recursos,
      });
    },
    [carregarPermissoes, novaGeracao],
  );

  const recarregar = useCallback(async () => {
    const minhaGeracao = geracao.current;

    const dados = await api<{
      user: SessionUser;
      tenant: TenantSummary | null;
      tenants: TenantSummary[];
    }>('/me');
    const acessos = dados.tenant ? await carregarPermissoes() : SEM_ACESSO;

    /*
     * Alguém saiu da conta enquanto isto ia e voltava? Então esta resposta é de
     * uma sessão que não existe mais, e escrevê-la poria a pessoa de volta
     * dentro do aplicativo — logada.
     *
     * Acontece de verdade: quem dispensa o tour dispara um `recarregar()` (para
     * o "já vi" valer na hora) e, tocando em "Sair da conta" logo em seguida,
     * era devolvido para dentro. No tablet que passa de mão em mão na
     * secretaria, é a conta de uma pessoa aberta para a seguinte.
     */
    if (geracao.current !== minhaGeracao) return;

    definirModoSimplificado(dados.user.modoSimplificado);
    lembrarSessao({
      usuario: dados.user,
      igreja: dados.tenant,
      igrejas: dados.tenants,
      permissoes: acessos.grants,
      recursos: acessos.recursos,
    });
    setEstado({
      carregando: false,
      usuario: dados.user,
      igreja: dados.tenant,
      igrejas: dados.tenants,
      permissoes: acessos.grants,
      recursos: acessos.recursos,
    });
  }, [carregarPermissoes]);

  const sair = useCallback(async () => {
    // Antes de qualquer coisa: o que estiver em voo não vale mais.
    novaGeracao();
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      definirToken(null);
      esquecerAcessoDeSuporte();
      /*
       * Ao sair, nada do que era daquela pessoa fica no aparelho: nem quem ela
       * é, nem a escala guardada para ler sem rede. O tablet da secretaria
       * passa de mão em mão.
       */
      esquecerSessao();
      limparOffline();
      setEstado({ ...ESTADO_INICIAL, carregando: false });
    }
  }, [novaGeracao]);

  const trocarIgreja = useCallback(
    async (tenantId: string) => {
      novaGeracao();
      const renovou = await renovarSessao(tenantId);
      if (renovou !== 'ok') throw new Error('Não foi possível trocar de igreja.');
      const dados = await api<{
        user: SessionUser;
        tenant: TenantSummary | null;
        tenants: TenantSummary[];
      }>('/me');
      const acessos = dados.tenant ? await carregarPermissoes() : SEM_ACESSO;
      setEstado({
        carregando: false,
        usuario: dados.user,
        igreja: dados.tenant,
        igrejas: dados.tenants,
        permissoes: acessos.grants,
        recursos: acessos.recursos,
      });
    },
    [carregarPermissoes, novaGeracao],
  );

  const adotarAcessoDeSuporte = useCallback(
    async (accessToken: string, impersonationId?: string) => {
      novaGeracao();
      definirToken(accessToken);
      if (impersonationId) lembrarAcessoDeSuporte(impersonationId);

      const dados = await api<{
        user: SessionUser;
        tenant: TenantSummary | null;
        tenants: TenantSummary[];
      }>('/me');
      const acessos = dados.tenant ? await carregarPermissoes() : SEM_ACESSO;

      setEstado({
        carregando: false,
        usuario: dados.user,
        igreja: dados.tenant,
        igrejas: dados.tenants,
        permissoes: acessos.grants,
        recursos: acessos.recursos,
      });
    },
    [carregarPermissoes, novaGeracao],
  );

  // Restaura a sessão pelo cookie de refresh: quem já entrou não vê a tela de
  // login de novo só porque atualizou a página.
  useEffect(() => {
    let ativo = true;

    aoExpirarSessao(() => {
      novaGeracao();
      definirToken(null);
      esquecerSessao();
      setEstado({ ...ESTADO_INICIAL, carregando: false });
    });

    const minhaGeracao = geracao.current;
    /** Esta restauração ainda é a que vale? */
    const atual = () => ativo && geracao.current === minhaGeracao;

    void (async () => {
      const renovou = await renovarSessao();
      if (!atual()) return;
      if (renovou !== 'ok') {
        /*
         * Sem rede, o Levitas abre com a última sessão conhecida — é o que
         * permite ver a própria escala no estacionamento da igreja (cenário
         * 20). O servidor recusando é outra história: aí a sessão acabou
         * mesmo, e o que estava guardado sai do aparelho.
         */
        const lembrada = renovou === 'sem-rede' ? sessaoLembrada() : null;
        if (!lembrada) {
          esquecerSessao();
          setEstado({ ...ESTADO_INICIAL, carregando: false });
          return;
        }

        definirModoSimplificado(lembrada.usuario.modoSimplificado);
        setEstado({ carregando: false, ...lembrada, recursos: lembrada.recursos ?? {} });
        return;
      }
      /*
       * Um acesso de suporte não sobrevive a um F5 sozinho: o token vive só na
       * memória, de propósito. O que fica guardado é o **identificador** do
       * acesso — inútil por si só. Reconstruí-lo exige a sessão de plataforma
       * de quem entrou **e** uma janela ainda aberta, que o servidor reconfere
       * antes de emitir outro token (M17).
       */
      const acessoDeSuporte = acessoDeSuporteLembrado();
      if (acessoDeSuporte) {
        try {
          const renovado = await api<{ accessToken: string }>(
            `/platform/impersonations/${acessoDeSuporte}/token`,
            { method: 'POST' },
          );
          definirToken(renovado.accessToken);
        } catch {
          esquecerAcessoDeSuporte();
        }
      }

      try {
        const dados = await api<{
          user: SessionUser;
          tenant: TenantSummary | null;
          tenants: TenantSummary[];
        }>('/me');
        const acessos = dados.tenant ? await carregarPermissoes() : SEM_ACESSO;
        if (!atual()) return;
        definirModoSimplificado(dados.user.modoSimplificado);
        lembrarSessao({
          usuario: dados.user,
          igreja: dados.tenant,
          igrejas: dados.tenants,
          permissoes: acessos.grants,
          recursos: acessos.recursos,
        });
        setEstado({
          carregando: false,
          usuario: dados.user,
          igreja: dados.tenant,
          igrejas: dados.tenants,
          permissoes: acessos.grants,
          recursos: acessos.recursos,
        });
      } catch {
        if (atual()) setEstado({ ...ESTADO_INICIAL, carregando: false });
      }
    })();

    return () => {
      ativo = false;
    };
  }, [carregarPermissoes, novaGeracao]);

  const valor = useMemo<ValorSessao>(
    () => ({
      ...estado,
      aplicar,
      trocarIgreja,
      adotarAcessoDeSuporte,
      recarregar,
      sair,
      /**
       * Esconder o que a pessoa não pode fazer é cortesia; a checagem que vale
       * é a do servidor, que roda sempre (seção 8.1).
       */
      pode: (permissao, escopoId = null) =>
        can(estado.permissoes, {
          permission: permissao,
          scope: { kind: escopoId ? 'ministry' : 'tenant', id: escopoId },
        }),
      podeEmAlgumEscopo: (permissao) => {
        const [recurso, acao] = permissao.split(':');
        return estado.permissoes.some((concessao) => {
          const [dela, acaoDela] = concessao.permission.split(':');
          return dela === recurso && (acaoDela === acao || acaoDela === 'manage');
        });
      },
      /*
       * Enquanto a sessão carrega, tudo é "tem": esconder um menu e devolvê-lo
       * um instante depois pisca a tela na cara de quem está usando.
       */
      temRecurso: (recurso) => estado.carregando || estado.recursos[recurso] !== false,
    }),
    [estado, aplicar, trocarIgreja, adotarAcessoDeSuporte, recarregar, sair],
  );

  return <ContextoSessao.Provider value={valor}>{children}</ContextoSessao.Provider>;
}
